import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment, Paper, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, TextField, Typography } from '@mui/material';
import { action, computed, observable, runInAction, makeObservable } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';
import { IParametrosUsuario, textosParametrosUsuarioColunas, textosParametrosUsuarioFiltros, textosParametrosUsuarioIcones } from '../../modelo/Parametros';
import { TipoEscaninho } from '../../modelo/TipoEscaninhoEnum';
import ParametroUsuarioService from '../../services/ParametroUsuarioService';
import MensagemStore from '../../store/MensagemStore';
import ParametroUsuarioStore from '../../store/ParametroUsuarioStore';
import RouterStore from '../../store/RouterStore';
import './css/TabelaParametrosSistemaStyles.css';
import Clear from '@mui/icons-material/Clear';

export interface IPropsTabelaParametrosUsuario {
    parametrosUsuario?: any;
    titulo?: string;
}

class TabelaParametrosUsuario extends React.Component<IPropsTabelaParametrosUsuario> {
    abaSelecionada: number = 0;
    mostrarDialogSigla: boolean = false;
    private forcarAtualizacao: number = 0; // Para forçar re-render

    constructor(props: IPropsTabelaParametrosUsuario) {
        super(props);

        makeObservable<TabelaParametrosUsuario, "forcarAtualizacao">(this, {
            abaSelecionada: observable,
            mostrarDialogSigla: observable,
            forcarAtualizacao: observable,
            colunas: computed,
            filtros: computed,
            icones: computed,
            handleMudaAba: action,
            handleChangeSwitch: action,
            atualizarParametroEmArray: action,
            abrirDialogSigla: action,
            fecharDialogSigla: action
        });
    }

    private agruparParametros(parametros: IParametrosUsuario[]) {
        // Forçar dependência do observable de atualização
        void this.forcarAtualizacao;

        if (!parametros) {
            return {};
        }
        return this.groupByParametro(Object.values(parametros));
    }

    get colunas() {
        return this.agruparParametros(ParametroUsuarioStore.parametrosColuna);
    }

    get filtros() {
        return this.agruparParametros(ParametroUsuarioStore.parametrosFiltro);
    }

    get icones() {
        return this.agruparParametros(ParametroUsuarioStore.parametrosIcone);
    }

    handleMudaAba = (e: React.SyntheticEvent, novaAbaSelecionada: number) => {
        this.abaSelecionada = novaAbaSelecionada;
    };

    groupByParametro = (data: any[]) => {
        if (!data || data.length === 0) return {};
        
        return data.reduce((acc: { [x: string]: any[]; }, item: { parametro: any; }) => {
            const { parametro } = item;
            if (!acc[parametro]) {
                acc[parametro] = [];
            }
            acc[parametro].push(item);
            return acc;
        }, {});
    };

    handleChangeSwitch = async (parametro: IParametrosUsuario) => {
        try {
            const novoValor = parametro.valor === 'true' ? 'false' : 'true';

            // Cria uma NOVA cópia do objeto para forçar reatividade
            const parametroAtualizado = {
                ...parametro,
                valor: novoValor
            };

            // Atualiza no servidor primeiro
            await ParametroUsuarioService.salvar(parametroAtualizado);

            // Atualiza no store DENTRO de runInAction
            runInAction(() => {
                // Atualiza no array principal
                const indexPrincipal = ParametroUsuarioStore.parametros.findIndex(
                    p => p.numParametro === parametro.numParametro
                );

                if (indexPrincipal !== -1) {
                    ParametroUsuarioStore.parametros[indexPrincipal] = parametroAtualizado;
                }

                // Atualiza nos arrays específicos (criando novos arrays para forçar reatividade)
                this.atualizarParametroEmArray(parametroAtualizado, 'parametrosColuna');
                this.atualizarParametroEmArray(parametroAtualizado, 'parametrosFiltro');
                this.atualizarParametroEmArray(parametroAtualizado, 'parametrosIcone');

                // Força atualização do componente
                this.forcarAtualizacao++;
            });

            MensagemStore.apresentarMensagem('Parâmetro salvo com sucesso.');

        } catch (error) {
            console.error('Erro ao salvar parâmetro:', error);
            MensagemStore.apresentarErro('Ocorreu um erro ao salvar o Parâmetro. Tente novamente, caso persista, contate o administrador.');
        }
    };

    atualizarParametroEmArray(parametroAtualizado: IParametrosUsuario, nomeArray: string) {
        const storeArray = ParametroUsuarioStore[nomeArray];
        
        if (storeArray && typeof storeArray === 'object') {
            // Se for um objeto (não array), precisamos encontrar a chave
            const entries = Object.entries(storeArray);
            let encontrado = false;
            
            const novoObjeto = entries.reduce((acc, [key, value]: [string, any]) => {
                if (value && value.numParametro === parametroAtualizado.numParametro) {
                    encontrado = true;
                    acc[key] = parametroAtualizado;
                } else {
                    acc[key] = value;
                }
                return acc;
            }, {} as any);
            
            if (encontrado) {
                ParametroUsuarioStore[nomeArray] = novoObjeto;
            }
        }
    }

    converteParametroColuna = (parametro: string) => {
        let retorno = '';
        switch (parametro) {
            case 'ojc':
                retorno = textosParametrosUsuarioColunas.OJC;
                break;
            case 'responsavel':
                retorno = textosParametrosUsuarioColunas.RESPONSAVEL;
                break;
            case 'movimentacao':
                retorno = textosParametrosUsuarioColunas.MOVIMENTACAO;
                break;
            case 'minutas':
                retorno = textosParametrosUsuarioColunas.MINUTAS;
                break;
            default:
                break;
        }
        return retorno;
    }

    converteParametroFiltro = (parametro: string) => {
        let retorno = '';
        switch (parametro) {
            case 'foraDoFluxo':
                retorno = textosParametrosUsuarioFiltros.FORA_DO_FLUXO;
                break;
            case 'assinatura':
                retorno = textosParametrosUsuarioFiltros.ASSINATURA;
                break;
            case 'origem':
                retorno = textosParametrosUsuarioFiltros.ORIGEM;
                break;
            case 'numeroProcesso':
                retorno = textosParametrosUsuarioFiltros.NUMERO_PROCESSO;
                break;
            case 'listaProcesso':
                retorno = textosParametrosUsuarioFiltros.LISTA_PROCESSO;
                break;
            case 'ojc':
                retorno = textosParametrosUsuarioFiltros.OJC_FILTRO;
                break;
            case 'andamento':
                retorno = textosParametrosUsuarioFiltros.ANDAMENTO;
                break;
            case 'movimentacao':
                retorno = textosParametrosUsuarioFiltros.MOVIMENTACAO;
                break;
            case 'anoAutuacao':
                retorno = textosParametrosUsuarioFiltros.ANO_AUTUACAO;
                break;
            case 'etiqueta':
                retorno = textosParametrosUsuarioFiltros.ETIQUETA;
                break;
            case 'responsavel':
                retorno = textosParametrosUsuarioFiltros.RESPONSAVEL;
                break;
            case 'relator':
                retorno = textosParametrosUsuarioFiltros.RELATOR;
                break;
            case 'possuiResponsavel':
                retorno = textosParametrosUsuarioFiltros.POSSUI_RESPONSAVEL;
                break;
            case 'indicador':
                retorno = textosParametrosUsuarioFiltros.INDICADOR;
                break;
            case 'classe':
                retorno = textosParametrosUsuarioFiltros.CLASSE;
                break;
            case 'assunto':
                retorno = textosParametrosUsuarioFiltros.ASSUNTO;
                break;
            case 'categoria':
                retorno = textosParametrosUsuarioFiltros.CATEGORIA;
                break;
            case 'inclusaoPauta':
                retorno = textosParametrosUsuarioFiltros.INCLUSAO_PAUTA;
                break;
            case 'movimento':
                retorno = textosParametrosUsuarioFiltros.MOVIMENTO;
                break;
            case 'partes':
                retorno = textosParametrosUsuarioFiltros.PARTES;
                break;
            case 'resultadoJulgamento':
                retorno = textosParametrosUsuarioFiltros.RESULTADO_JULGAMENTO;
                break;
            case 'situacaoMinuta':
                retorno = textosParametrosUsuarioFiltros.SITUACAO_MINUTA;
                break;
            case 'peticao':
                retorno = textosParametrosUsuarioFiltros.PETICAO;
                break;
            case 'impedimento':
                retorno = textosParametrosUsuarioFiltros.IMPEDIMENTO;
                break;
            case 'tipoJulgamento':
                retorno = textosParametrosUsuarioFiltros.TIPO_JULGAMENTO;
                break;
            case 'possuiMinuta':
                retorno = textosParametrosUsuarioFiltros.POSSUI_MINUTA;
                break;
            case 'unidadePublicadora':
                retorno = textosParametrosUsuarioFiltros.UNIDADE_PUBLICADORA;
                break;
            case 'publicavel':
                retorno = textosParametrosUsuarioFiltros.PUBLICAVEL;
                break;
            case 'correJuntoAssociado':
                retorno = textosParametrosUsuarioFiltros.CORRE_JUNTO_ASSOCIADO;
                break;
            case 'estrela':
                retorno = textosParametrosUsuarioFiltros.FAVORITO;
                break;
            case 'redator':
                retorno = textosParametrosUsuarioFiltros.REDATOR;
                break;
            case 'retornoGabinete':
                retorno = textosParametrosUsuarioFiltros.RETORNO_GABINETE;
                break;
            case 'triagemLivre':
                retorno = textosParametrosUsuarioFiltros.TRIAGEM_LIVRE;
                break;
            case 'tiposPeticoesPendentes':
                retorno = textosParametrosUsuarioFiltros.TIPOS_PETICOES_PENDENTES;
                break;
            case 'votoLiberado':
                retorno = textosParametrosUsuarioFiltros.VOTO_LIBERADO;
                break;
            default:
                break;
        }
        return retorno;
    }

    converteParametroIcone = (parametro: string) => {
        let retorno = '';
        switch (parametro) {
            case 'iconeOrigem':
                retorno = textosParametrosUsuarioIcones.ICONE_ORIGEM;
                break;
            case 'ge':
                retorno = textosParametrosUsuarioIcones.ICONE_GE;
                break;
            case 'espelho':
                retorno = textosParametrosUsuarioIcones.ESPELHO;
                break;
            case 'bemTeVi':
                retorno = textosParametrosUsuarioIcones.BEM_TE_VI;
                break;
            case 'adicionarEtiqueta':
                retorno = textosParametrosUsuarioIcones.ADICIONAR_ETIQUETA;
                break;
            case 'adicionarTriagem':
                retorno = textosParametrosUsuarioIcones.ADICIONAR_TRIAGEM;
                break;
            case 'adicionarMovimentacao':
                retorno = textosParametrosUsuarioIcones.ADICIONAR_MOVIMENTACAO;
                break;
            case 'atualizarDados':
                retorno = textosParametrosUsuarioIcones.ATUALIZAR_DADOS;
                break;
            case 'copiarNumero':
                retorno = textosParametrosUsuarioIcones.COPIAR_NUMERO;
                break;
            case 'resumoAguia':
                retorno = textosParametrosUsuarioIcones.RESUMO_AGUIA;
                break;
            case 'inteiroTeor':
                retorno = textosParametrosUsuarioIcones.INTEIRO_TEOR;
                break;
            case 'peticoesPendentes':
                retorno = textosParametrosUsuarioIcones.PETICOES_PENDENTES;
                break;
            case 'retornoGabinete':
                retorno = textosParametrosUsuarioIcones.RETORNO_GABINETE;
                break;
            case 'impedimento':
                retorno = textosParametrosUsuarioIcones.IMPEDIMENTO;
                break;
            case 'relatorOriginal':
                retorno = textosParametrosUsuarioIcones.RELATOR_ORIGINAL;
                break;
            case 'associado':
                retorno = textosParametrosUsuarioIcones.ASSOCIADO;
                break;
            case 'correJunto':
                retorno = textosParametrosUsuarioIcones.CORRE_JUNTO;
                break;
            default:
                break;
        }
        return retorno;
    }

    irParaTelaPrincipal = () => {
        RouterStore.push('/telaPrincipal');
    }

    retornaTipoEscaninho(tipo) {
        switch (tipo) {
            case TipoEscaninho.GERENCIAL.id:
                return TipoEscaninho.GERENCIAL.descricao;
            case TipoEscaninho.USUARIO.id:
                return TipoEscaninho.USUARIO.descricao;
            case TipoEscaninho.VISTOS.id:
                return TipoEscaninho.VISTOS.descricao;
            case TipoEscaninho.JULGAMENTO.id:
                return TipoEscaninho.JULGAMENTO.descricao;
            case TipoEscaninho.PETICOES.id:
                return TipoEscaninho.PETICOES.descricao;
            case TipoEscaninho.REDATOR_DESIGNADO.id:
                return TipoEscaninho.REDATOR_DESIGNADO.descricao;
            case TipoEscaninho.JUSTIFICATIVA_VOTO_VENCIDO.id:
                return TipoEscaninho.JUSTIFICATIVA_VOTO_VENCIDO.descricao;
            default:
                return '';
        }
    }

    renderDialogSigla() {
        return (
            <Dialog
                open={ParametroUsuarioStore.exibirDialogSigla}
                onClose={() =>
                    runInAction(() => {
                        ParametroUsuarioStore.exibirDialogSigla = false;
                    })
                }
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: "bold" }}>
                    Cadastrar sigla de usuário
                </DialogTitle>
                <DialogContent>
                    <Typography
                        variant="body2"
                        sx={{
                            mt: 1,
                            lineHeight: 1.5,
                        }}
                    >
                        Agora você pode criar uma{" "}
                        <strong>sigla de identificação</strong> vinculada ao seu usuário.{" "}
                        Ela poderá ser utilizada em modelos através da variável <strong>{"${SIGLAUSUARIO}"}</strong>.
                    </Typography>
                </DialogContent>

                <DialogActions>
                    <Button
                        onClick={() =>
                            runInAction(() => {
                                ParametroUsuarioStore.exibirDialogSigla = false;
                            })
                        }
                        color="inherit"
                    >
                        Cancelar
                    </Button>

                    <Button
                        onClick={() =>
                            runInAction(() => {
                                ParametroUsuarioStore.exibirDialogSigla = false;
                                this.abaSelecionada = 3;
                            })
                        }
                        variant="contained"
                        color="primary"
                    >
                        Criar agora
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    abrirDialogSigla() {
        this.mostrarDialogSigla = true;
    }

    fecharDialogSigla() {
        this.mostrarDialogSigla = false;
    }

    renderTabelaParametros(
        titulo: string,
        agrupados: { [parametro: string]: IParametrosUsuario[] },
        converter: (parametro: string) => string,
        sufixoIdCelula: string,
        infixoChaveItem: string
    ) {
        return (
            <>
                <span><h3>{titulo}</h3></span>
                <TableContainer style={{ height: '65vh' }}>
                    <Table stickyHeader aria-label="sticky table">
                        <TableHead>
                            <TableRow>
                                <TableCell style={{ width: '25%', fontWeight: 'bold' }}>Nome</TableCell>
                                <TableCell style={{ width: '25%', fontWeight: 'bold' }}>Exibir</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {Object.keys(agrupados).map((chave, index) => {
                                const itens = agrupados[chave] || [];

                                return (
                                    <TableRow key={`${chave}-${index}`}>
                                        <TableCell>{converter(chave)}</TableCell>
                                        <TableCell id={`acoesParametroSistema${sufixoIdCelula}-${index}`} style={{ display: 'flex', flexDirection: 'column' }}>
                                            {itens.filter(item => {
                                                return item.codTipoEscaninho === 1 || item.codTipoEscaninho === 2;
                                            }).map((item: IParametrosUsuario) => (
                                                <div style={{ display: 'flex', alignItems: 'center' }} key={`item-${infixoChaveItem}${item.numParametro}`}>
                                                    <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                                                        <Checkbox
                                                            checked={item.valor === 'true'}
                                                            title={this.retornaTipoEscaninho(item.codTipoEscaninho)}
                                                            onChange={(e) => {
                                                                e.stopPropagation();
                                                                this.handleChangeSwitch(item);
                                                            }}
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                    </div>
                                                    {this.retornaTipoEscaninho(item.codTipoEscaninho)}
                                                </div>
                                            ))}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </>
        );
    }

    render() {
        const colunasAgrupadas = this.colunas || {};
        const filtrosAgrupados = this.filtros || {};
        const iconesAgrupados = this.icones || {};

        return (
            <span style={{ display: 'flex', flexDirection: 'column' }}>
                <Paper sx={{ paddingTop: 3, paddingBottom: 0, paddingLeft: 0.1 }}>
                    {ParametroUsuarioStore.exibirDialogSigla && this.renderDialogSigla()}
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs
                            value={this.abaSelecionada}
                            onChange={this.handleMudaAba}
                            centered
                            sx={{
                                minHeight: 42,
                                "& .MuiTab-root": {
                                    minHeight: 42,
                                    flex: '100%',
                                    padding: "8px 32px",
                                    color: "#6b7280",
                                    fontWeight: 500,
                                    borderRadius: "8px 8px 0 0",
                                    transition: "all 0.3s ease",
                                    alignItems: "center",
                                },
                                "& .MuiTab-root:not(.Mui-selected):hover": {
                                    backgroundColor: "#bec3d194",
                                },
                                "& .MuiTab-root.Mui-selected": {
                                    backgroundColor: "primary.main",
                                    color: "#ffffff",
                                    fontWeight: "bold",
                                },
                                "& .MuiTabs-flexContainer": {
                                    gap: "1px",
                                },
                                "& .MuiTabs-indicator": {
                                    display: "none",
                                },
                            }}
                        >
                            <Tab label="Configuração de Colunas" />
                            <Tab label="Configuração de Filtros" />
                            <Tab label="Configuração de Funcionalidades/ Ícones" />
                            <Tab label="Configuração de Sigla do Usuário" />
                        </Tabs>
                    </Box>
                </Paper>
                <Paper style={{ padding: 5 }}>
                    <div className='divTabela' id='parametrosSistema'>
                        {this.abaSelecionada === 0 &&
                            this.renderTabelaParametros('Configuração de Colunas', colunasAgrupadas, this.converteParametroColuna, '', '')
                        }
                        {this.abaSelecionada === 1 &&
                            this.renderTabelaParametros('Configuração de Filtros', filtrosAgrupados, this.converteParametroFiltro, 'Filtro', 'filtro-')
                        }
                        {this.abaSelecionada === 2 &&
                            this.renderTabelaParametros('Configuração de Funcionalidades / Ícones', iconesAgrupados, this.converteParametroIcone, 'Icone', 'icone-')
                        }
                        {this.abaSelecionada === 3 && (
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    height: "70.8vh",
                                    gap: "16px",
                                }}
                            >
                                {ParametroUsuarioStore.siglaSalva ? (
                                    <h3>
                                        Sigla do usuário:{" "}
                                        <span style={{ fontWeight: "bold", color: "#1976d2" }}>
                                            {ParametroUsuarioStore.siglaSalva}
                                        </span>
                                    </h3>
                                ) : (
                                    <h3>Crie sua sigla</h3>
                                )}

                                <TextField
                                    color="primary"
                                    label={
                                        ParametroUsuarioStore.siglaSalva
                                            ? ParametroUsuarioStore.sigla.length > 0 &&
                                                ParametroUsuarioStore.sigla.length < 2
                                                ? "Nova sigla (mínimo 2 caracteres)"
                                                : "Nova sigla"
                                            : ParametroUsuarioStore.sigla.length > 0 &&
                                                ParametroUsuarioStore.sigla.length < 2
                                                ? "Sigla (mínimo 2 caracteres)"
                                                : "Sigla"
                                    }
                                    variant="outlined"
                                    size="small"
                                    value={ParametroUsuarioStore.sigla}
                                    onChange={(e) => {
                                        const valorDigitado = e.target.value;

                                        const invalido = /[^A-Za-z0-9_/-]/.test(valorDigitado);

                                        if (invalido) {
                                            const input = e.target;
                                            input.classList.add("shake");
                                            setTimeout(() => input.classList.remove("shake"), 300);
                                        }

                                        let valorFiltrado = valorDigitado.replace(/[^A-Za-z0-9_/-]/g, "");

                                        const primeirosDois = valorFiltrado.slice(0, 2);

                                        if (/^[/_-]{2}$/.test(primeirosDois)) {
                                            const input = e.target;
                                            input.classList.add("shake");
                                            setTimeout(() => input.classList.remove("shake"), 300);
                                            valorFiltrado = "";
                                        }

                                        ParametroUsuarioStore.setSigla(valorFiltrado);
                                    }}
                                    inputProps={{
                                        maxLength: 40,
                                    }}
                                    sx={{
                                        width: 250,
                                        "& label": {
                                            color:
                                                !ParametroUsuarioStore.sigla
                                                    ? "#757575"
                                                    : ParametroUsuarioStore.sigla.length < 2
                                                        ? "#757575"
                                                        : "#2e7d32",
                                            transition: "color 0.3s ease",
                                        },
                                        "& label.Mui-focused": {
                                            color:
                                                !ParametroUsuarioStore.sigla
                                                    ? "#1976d2"
                                                    : ParametroUsuarioStore.sigla.length < 2
                                                        ? "#1976d2"
                                                        : "#2e7d32",
                                        },
                                        "& .MuiOutlinedInput-root": {
                                            "& fieldset": {
                                                borderColor:
                                                    !ParametroUsuarioStore.sigla
                                                        ? "#c4c4c4"
                                                        : ParametroUsuarioStore.sigla.length < 2
                                                            ? "#c4c4c4"
                                                            : "#2e7d32",
                                                transition: "border-color 0.3s ease",
                                            },
                                            "&:hover fieldset": {
                                                borderColor:
                                                    !ParametroUsuarioStore.sigla
                                                        ? "#757575"
                                                        : ParametroUsuarioStore.sigla.length < 2
                                                            ? "#757575"
                                                            : "#2e7d32",
                                            },
                                            "&.Mui-focused fieldset": {
                                                borderColor:
                                                    !ParametroUsuarioStore.sigla
                                                        ? "#1976d2"
                                                        : ParametroUsuarioStore.sigla.length < 2
                                                            ? "#1976d2"
                                                            : "#2e7d32",
                                            },
                                        },

                                        "@keyframes shake": {
                                            "0%, 100%": { transform: "translateX(0)" },
                                            "20%, 60%": { transform: "translateX(-4px)" },
                                            "40%, 80%": { transform: "translateX(4px)" },
                                        },
                                        "& input.shake": {
                                            animation: "shake 0.3s ease",
                                        },
                                    }}
                                    InputProps={{
                                        endAdornment: ParametroUsuarioStore.sigla ? (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => ParametroUsuarioStore.limparSigla()}
                                                    edge="end"
                                                    sx={{
                                                        color: "#6b7280",
                                                        "&:hover": { color: "#d32f2f" },
                                                    }}
                                                >
                                                    <Clear fontSize="small" />
                                                </IconButton>
                                            </InputAdornment>
                                        ) : null,
                                    }}
                                />

                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => ParametroUsuarioStore.salvarSigla()}
                                    disabled={
                                        !ParametroUsuarioStore.sigla ||
                                        ParametroUsuarioStore.sigla.trim().length < 2 ||
                                        (
                                            ParametroUsuarioStore.siglaSalva &&
                                            ParametroUsuarioStore.sigla.trim() ===
                                            ParametroUsuarioStore.siglaSalva.trim()
                                        )
                                    }
                                    sx={{ mt: 1 }}
                                >
                                    {ParametroUsuarioStore.siglaSalva ? "Alterar" : "Criar"}
                                </Button>
                            </div>
                        )}
                    </div>
                </Paper>
                <span style={{ display: 'flex', justifyContent: 'center' }}>
                    <Button color="primary"
                        variant="outlined"
                        onClick={this.irParaTelaPrincipal}
                        title={'Sair'}
                        style={{ marginTop: '20px', marginLeft: '20px' }}
                    >
                        Voltar
                    </Button>
                </span>
            </span>
        );
    }
}

export default observer(TabelaParametrosUsuario);
